import { forwardRef, useContext, useEffect, useState } from 'react';
import { Badge, Box, Grid, Step, StepConnector, StepLabel, Stepper, Typography, useMediaQuery } from '@mui/material';
import { makeStyles, styled } from '@mui/styles';
import { AreaContext } from '../store/AreaContext';
import { PLAY, SAVE_DESTINATION } from '../store/AreaReducers';
import { isMobile } from 'react-device-detect';
import { useQuery } from 'react-query';
import { strapiRequest } from '../functions/axiosRequest';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTree } from '@fortawesome/pro-light-svg-icons';
import { LanguageContext } from '../context/LanguageContext';
import { FilterContext } from "../context/FilterContext";
import filterHotspot from '../functions/filterHotspot';

const useStyles = makeStyles(theme => ({
	root: {
		position: 'absolute',
		width: '100%'
	},
	container: {
		width: '100%',
		height: '0px',
		position: 'relative',
		paddingTop: '56.25%',
	},
	sub_area: {
		height: '100%',
		width: '100vw',
		'&:hover': {
			cursor: 'pointer'
		}
	}
}));

const Timeline = forwardRef(({ area }, ref) => {

	const locale=useContext(LanguageContext);

	const classes = useStyles();
	const { dispatchTransition, dispatchDestination } = useContext(AreaContext);
	const xlDown = useMediaQuery((theme) => theme.breakpoints.down('xl'));

	//Timelines request - re-fires when language or timeline is changed
	const { status, data, error, isFetching, isLoading } = useQuery(
		['timeline', area], //Query key and dependencies
		()=>strapiRequest({ //Query function
			method: 'get',
			endpoint: `timelines/${area.area_type[0].timeline?.documentId}`,
			params: {
				sort: 'id:asc',
				locale: locale.lang.code
			}
		}),
		{ //Query options
			retry: 1,
			enabled: !!area.area_type[0].timeline?.documentId
		}
	);

	const [timelinePoints, setTimelinePoints] = useState();
	const [currentPoint, setCurrentPoint] = useState();

	//create points including active, next and previous
	useEffect(() => {
		if (data && area) {
			let copy = structuredClone(data.data.timeline_points)
			//find current
			let index = copy.findIndex((point) => point.page.documentId === area.documentId)
			//matching page found
			if (index > -1) {
				copy[index].active = true
			}
			//add previous
			if (index > 0) {
				copy[index - 1].previous = true
			}
			//and next
			if (index + 1 !== copy.length) {
				copy[index + 1].next = true
			}
			setTimelinePoints(copy)
			setCurrentPoint(copy[index])
		}
	}, [data, area]);

	const handleTimelineClick = (point) => {
		//Save the destination area
		let transition_url
		if (point.previous) {
			transition_url = currentPoint.transition_prev?.url
		} else if (point.next) {
			transition_url = currentPoint.transition_next?.url
		} else {
			// not prevoius or next, so do a fade to page
			transition_url = null
		}
		dispatchDestination({
			type: SAVE_DESTINATION,
			payload: {
				to: point.page.slug,
				transition_url: transition_url
			}
		});
		dispatchTransition({
			type: PLAY,
			payload: {
				url: transition_url,
				to: point.page.slug,
			}
		});
		setTimelinePoints();
	};

	const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
		top: isMobile ? 25 : xlDown? 40 : 50,
		height: 4,
		border: 0,
		backgroundColor: '#FFF',
		borderRadius: 0,
		width: isMobile ? '28px' : xlDown ? '45px' : '80px',
		left: isMobile ? '-13px' : xlDown ? '-22px' : '-43px',
		'& span': {
			display: 'none'
		}
	}));

	const CustomIcon = ({ point }) => { 

		const filters = useContext(FilterContext);

		const hasPage = point?.page?.hotspots?.[0]?.type?.[0]?.__component === 'hotspot.navigator';

		const allPoints = hasPage?
			point?.page?.hotspots?.[0]?.type?.[0]?.area?.hotspots
		:
			point?.page?.hotspots;

		const [filteredPoints, setFilteredPoints] = useState(allPoints);

		useEffect(() => {
			const filtered = allPoints.filter((point) => filterHotspot(point, filters));
			setFilteredPoints(filtered);
		}, [allPoints, filters]);

		return (
			<Badge
				badgeContent={filteredPoints?.length}
				showZero
				color={"primary"}
				sx={{
					'& .MuiBadge-badge':{
						fontSize:'1rem',
						fontWeight:600,
						width:'25px',
						height:'25px',
						borderRadius:'50%'
					}
				}}
			>
				<Box 
					sx={{
						borderColor: '#FFF',
						borderWidth: '4px',
						borderStyle: 'solid',
						borderRadius: '30px',
						width: isMobile ? 50 : xlDown ? 85 : 100,
						height: isMobile ? 50 : xlDown ? 85 : 100,
						display: 'flex',
						borderRadius: '50%',
						justifyContent: 'center',
						alignItems: 'center',
						cursor: !point.active ? 'pointer' : 'default',
						'& > .MuiBox-root': {
							backgroundColor: point.active ? '#f7cd36' : '#008c9d',
							transition: '0.1s ease background-color',
						}
					}}
					onClick={() => {
						if (!point.active) {
							handleTimelineClick(point)
						}
					}}
				>
					<Box 
						sx={{
							borderRadius: '50%',
							width: isMobile ? 40 : xlDown ? 50 : 75,
							height: isMobile ? 40 : xlDown ? 50 : 75,
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							'& svg': {
								color: '#FFF',
								fill: '#FFF'
							}
						}}
					>
						{point?.icon?.url ?
							<img
								style={{
									width: isMobile ? 35 : xlDown ? 45 : 70,
									height: isMobile ? 35 : xlDown ? 45 : 70,
								}}
								alt="certis"
								src={point.icon.url}
							/>
							:
							<FontAwesomeIcon icon={faTree} size='2x' />
						}
					</Box>
				</Box>
			</Badge>
		);
	}

	return (
		<Grid container
			className={classes.root}
			sx={{
				top: '24%'
			}}
			justifyContent="center">
			<Stepper alternativeLabel connector={<ColorlibConnector />}>
				{timelinePoints ?
					timelinePoints.map((point, i) => {
						return (
							<Step key={i} sx={{
								width: isMobile ? 100 : xlDown ? 150 : 200,
								cursor: 'pointer',
								'&:hover p': {
									textShadow: '1px 1px 3px #ffffff9f'
								}
							}}>
								<StepLabel
									sx={{
										'&:hover .MuiStepLabel-iconContainer > .MuiBox-root > .MuiBox-root': {
											backgroundColor: point.active ? '#f7cd36' : '#015560',
										},
									}}
									StepIconComponent={() => <CustomIcon point={point} />}
								>
									<Box
										sx={{
											p:'5px',
											borderRadius:'5px',
											background:'#008c9d'
										}}
									>
										<Typography
											sx={{
												color:'#fff',
												cursor: !point.active ? 'pointer' : 'default',
												fontWeight: 700,
												transition: '0.3s ease text-shadow',
												fontSize:{
													xs:'0.6rem',
													md:'1rem'
												}
											}}
											component="p"
											variant={isMobile ? "caption" : xlDown ? "body1" : "h6"}
										>
											{point.title}
										</Typography>
									</Box>
								</StepLabel>
							</Step>
						)
					})
					: null}
			</Stepper>
		</Grid>
	);
}
);

export default Timeline;
